import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import {
  INVALID_BOOKING_ID,
  MOBILE_TOOLTIP_MSG,
} from '@shared/constant/shared.constant';
import { TruxService } from '@core/services/trux.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastService } from '@shared/services/toast.service';
import { emailOrPhoneValidator } from '@shared/services/custom-validatior.abstract';
import { autoUnsubWith } from '@shared/services/utility.abstract';

@Component({
  selector: 'app-trux-login',
  templateUrl: './trux-login.component.html',
  styleUrls: ['./trux-login.component.scss'],
})
export class TruxLogin implements OnInit {
  readonly destroyRef = inject(DestroyRef);
  mobileMsg: string = MOBILE_TOOLTIP_MSG;
  truxLoginForm: FormGroup;

  constructor(
    private readonly truxService: TruxService,
    private readonly formBuilder: FormBuilder,
    private readonly router: Router,
    private readonly toastService: ToastService
  ) { }

  ngOnInit(): void {
    this.initLoginForm();
  }

  private initLoginForm(): void {
    this.truxLoginForm = this.formBuilder.group({
      email: ['', [Validators.required, emailOrPhoneValidator]],
      password: ['', [Validators.required]],
    });
  }

  truxLogin(): void {
    if (!this.validateForm()) {
      return;
    }

    const { email, password } = this.truxLoginForm.value;
    const request = { email, password };

    this.truxService
      .authUserActiveBooking(request)
      .pipe(autoUnsubWith(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.handleAuthResponse(response);
        },
        error: (error) => {
          this.handleError(error);
        },
      });
  }

  private validateForm(): boolean {
    if (this.truxLoginForm.invalid) {
      this.truxLoginForm.markAllAsTouched();
      return false;
    }
    return true;
  }

  private handleAuthResponse(response): void {
    const bookingId: string = response['encryptBookingId'];
    const successMessage: string = response['message'];

    if (bookingId) {
      this.toastService.showToastMessage('success', 'Success', successMessage);
      this.router.navigate([`/gatetrux/${bookingId}`]);
    } else {
      this.toastService.showToastMessage('error', 'Error', INVALID_BOOKING_ID);
    }
  }

  private handleError(error: any): void {
    const message: string = error?.error?.message || 'Login failed';
    this.toastService.showToastMessage('error', 'Error', message);
  }

  getInvalidClassFor(controlName: 'email' | 'password') {
    const control = this.truxLoginForm.get(controlName);
    const isInvalid =
      control?.hasError('required') && (control.dirty || control.touched);
    return {
      'ng-invalid': isInvalid,
      'ng-dirty': isInvalid,
    };
  }
}
